/**
 * Мапа сіда: the whole country a seed makes, before you walk into it.
 *
 * Drawn by the game's own generator running in workers - see `tile.worker`
 * for why that matters and what it costs. This file is the part a person
 * touches: where the view is, which tiles that needs, and what is drawn on
 * top of them.
 *
 * The view is kept in *blocks*, not pixels. Zooming changes how many blocks
 * a pixel stands for and nothing else, so the thing under the cursor stays
 * under the cursor and every coordinate on screen is a coordinate you can
 * fly to.
 */
import {
  TILE_PX, type TileReply, type TileRequest, type WorldFacts,
  type TreasureFacts,
} from "./tile.worker.js";
import {
  planVillage, VILLAGE_REACH, type Kind,
} from "../core/world/village.js";

const canvas = document.getElementById("map") as HTMLCanvasElement;
const ctx = canvas.getContext("2d")!;
const seedBox = document.getElementById("seed") as HTMLInputElement;
const scaleLabel = document.getElementById("scale") as HTMLSpanElement;
const whereBox = document.getElementById("where") as HTMLDivElement;
const legendBox = document.getElementById("legend") as HTMLDivElement;
const busy = document.getElementById("busy") as HTMLDivElement;

/**
 * Blocks to the pixel, finest first. One is a block per pixel.
 *
 * Below one is a block drawn as several pixels, which the world costs
 * nothing extra for - a quarter-block step probes the same column four times
 * over - and is the only way to look at a село. A village is about a hundred
 * and forty blocks across: at one block to the pixel that is a thumbnail the
 * size of a postage stamp, and "хочу подивитись як вони виглядають" wants
 * the хати to be хати.
 */
const STEPS = [0.25, 0.5, 1, 2, 4, 8, 16, 32] as const;

let seed = 20260905;
let step = 8;
/** The block at the middle of the window. */
let midX = 2048;
let midZ = 2048;
let facts: WorldFacts | null = null;
/** Filled in a few seconds after the rest: see the worker. */
let treasures: TreasureFacts["list"] = [];
/** Bumped whenever the view changes, so late tiles can be dropped. */
let epoch = 0;

const tiles = new Map<string, ImageBitmap | "asked">();
const tileKey = (tx: number, tz: number, s: number): string => `${s}/${tx},${tz}`;

/*
 * A worker for each core, minus one for the page itself.
 *
 * Tiles are independent, so this is the rare case where more workers is
 * simply faster. Four are plenty: the whole visible window at eight blocks a
 * pixel is a few dozen tiles.
 */
const COUNT = Math.max(2, Math.min(4, (navigator.hardwareConcurrency ?? 4) - 1));
const pool: Worker[] = [];
let nextWorker = 0;
let pending = 0;

function startPool(): void {
  for (const w of pool) w.terminate();
  pool.length = 0;
  tiles.clear();
  treasures = [];
  pending = 0;
  for (let i = 0; i < COUNT; i++) {
    const worker = new Worker(new URL("./tile.worker.ts", import.meta.url),
      { type: "module" });
    worker.onmessage = (e: MessageEvent<TileReply | WorldFacts | TreasureFacts>) => {
      const message = e.data;
      if (message.t === "world") {
        facts = message;
        draw();
        say();
        return;
      }
      if (message.t === "treasures") {
        // Ignored if the seed has moved on while it was being dug up.
        if (message.seed === seed) { treasures = message.list; draw(); say(); }
        return;
      }
      pending = Math.max(0, pending - 1);
      busy.hidden = pending === 0;
      /*
       * Every tile that comes back is kept, however old the request.
       *
       * This used to throw away any reply from before the last pan or zoom -
       * and left the `"asked"` marker sitting in the map, so that tile was
       * never requested again. Three quick clicks on the zoom and the whole
       * window was permanently stuck on the blown-up coarse version, which
       * is exactly what it looked like: a map that never sharpened. A tile
       * is keyed by its own zoom and position, so it cannot go stale - it is
       * either the country there or it is not.
       */
      const pixels = new Uint8ClampedArray(message.pixels);
      createImageBitmap(new ImageData(pixels, TILE_PX, TILE_PX)).then((bmp) => {
        tiles.set(tileKey(message.tx, message.tz, message.step), bmp);
        draw();
      });
    };
    pool.push(worker);
  }
  // Only one of them grows the world and reports where things are; the rest
  // never call generate() at all. See the worker.
  pool[0]!.postMessage({ t: "world", seed });
}

function ask(tx: number, tz: number): void {
  const key = tileKey(tx, tz, step);
  if (tiles.has(key)) return;
  tiles.set(key, "asked");
  const request: TileRequest = { t: "tile", seed, tx, tz, step, epoch };
  pool[nextWorker % pool.length]!.postMessage(request);
  nextWorker++;
  pending++;
  busy.hidden = false;
}

/** Block -> screen, and back. */
const toScreenX = (x: number): number =>
  (x - midX) / step + canvas.width / (2 * devicePixelRatio);
const toScreenZ = (z: number): number =>
  (z - midZ) / step + canvas.height / (2 * devicePixelRatio);
const toBlockX = (sx: number): number =>
  midX + (sx - canvas.width / (2 * devicePixelRatio)) * step;
const toBlockZ = (sz: number): number =>
  midZ + (sz - canvas.height / (2 * devicePixelRatio)) * step;

function resize(): void {
  const ratio = devicePixelRatio || 1;
  canvas.width = Math.floor(innerWidth * ratio);
  canvas.height = Math.floor(innerHeight * ratio);
  canvas.style.width = `${innerWidth}px`;
  canvas.style.height = `${innerHeight}px`;
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.imageSmoothingEnabled = false;
  draw();
}

/**
 * What the last frame was actually made of.
 *
 * Put on the window because it is the only way to tell the two kinds of full
 * screen apart: a window of real tiles and a window of coarse ones blown up
 * are both country, both the right country, and pixel counting cannot
 * separate them. The map froze on placeholders once and every check passed.
 */
declare global {
  interface Window {
    __mapTiles?: { step: number; real: number; standIn: number };
    __mapView?: { midX: number; midZ: number; step: number };
  }
}
function draw(): void {
  const w = innerWidth, h = innerHeight;
  let real = 0, standIn = 0;
  ctx.fillStyle = "#0e1116";
  ctx.fillRect(0, 0, w, h);

  // Which tiles the window covers, at this zoom.
  const span = TILE_PX * step;
  const x0 = Math.floor(toBlockX(0) / span);
  const x1 = Math.floor(toBlockX(w) / span);
  const z0 = Math.floor(toBlockZ(0) / span);
  const z1 = Math.floor(toBlockZ(h) / span);
  for (let tz = z0; tz <= z1; tz++) {
    for (let tx = x0; tx <= x1; tx++) {
      const got = tiles.get(tileKey(tx, tz, step));
      const sx = toScreenX(tx * span);
      const sz = toScreenZ(tz * span);
      if (got === undefined) ask(tx, tz);
      if (got !== undefined && got !== "asked") {
        ctx.drawImage(got, sx, sz, TILE_PX, TILE_PX);
        real++;
        continue;
      }
      standIn++;
      coarse(tx, tz, sx, sz);
    }
  }

  window.__mapTiles = { step, real, standIn };
  // Where the window actually is, for the same reason `__mapTiles` exists:
  // "did the button move the view" cannot be answered from the DOM, and a
  // check that reads the text panel instead passes whether it moved or not.
  window.__mapView = { midX, midZ, step };
  if (facts !== null) {
    /*
     * Villages first, so a ring for the start or the gate is never hidden
     * under one. There are dozens of them and two of the others.
     */
    for (const v of facts.villages) {
      if (step <= PLAN_VILLAGES) drawVillage(v);
      mark(v.x, v.z, "#9bd150", "село", step <= LABEL_VILLAGES ? "село" : "");
    }
    /*
     * And the скарби, which are the one thing on this map you cannot see
     * from the ground at all: they are five to fifteen blocks under it.
     * The label is the depth, because that is the part worth knowing before
     * you start digging.
     */
    /*
     * The лігва. Fewer than anything else on this map and the only thing on
     * it that is looking for you: the label carries the y, because the hall
     * is thirteen blocks inside a mountain and the mouth is somewhere on the
     * hillside below it.
     */
    for (const l of facts.lairs) {
      mark(l.x, l.z, "#ff6a3d", "лігво",
        step <= LABEL_VILLAGES ? "лігво" : "");
    }
    for (const t of treasures) {
      mark(t.x, t.z, "#ffc83d", "скарб",
        step <= LABEL_VILLAGES ? `−${t.depth}` : "");
    }
    mark(facts.home.x, facts.home.z, "#ffd94a", "старт");
    if (facts.gate !== null) {
      mark(facts.gate.x, facts.gate.z, "#54c8ff", "замок Чахлика");
    }
  }
  legend();
}

/**
 * While a tile is being worked out, show the same ground from further away.
 *
 * A tile at one block to the pixel is sixteen thousand probes - a good part
 * of a second - so a window of them is several seconds of blank screen, and
 * zooming in looked like the page had broken. The coarser tiles are already
 * drawn and already in hand: the right quarter of the right one, blown up,
 * is the same country at less detail. It sharpens in place as the real tiles
 * land, and nothing is ever empty.
 */
function coarse(tx: number, tz: number, sx: number, sz: number): void {
  for (const bigger of STEPS) {
    if (bigger <= step) continue;
    const k = bigger / step;
    const bmp = tiles.get(tileKey(Math.floor(tx / k), Math.floor(tz / k), bigger));
    if (bmp === undefined || bmp === "asked") continue;
    // Which part of that tile is this one: the fraction left over after the
    // whole tiles it contains.
    const fx = ((tx % k) + k) % k;
    const fz = ((tz % k) + k) % k;
    const size = TILE_PX / k;
    ctx.drawImage(bmp, fx * size, fz * size, size, size,
      sx, sz, TILE_PX, TILE_PX);
    return;
  }
}

/**
 * How far in a village gets its name written next to it.
 *
 * At eight blocks a pixel the window is most of the world and every село in
 * it is on screen at once: fifty names over a map of fifty rings is not a
 * label, it is a hedge. The ring is always there to find; the word arrives
 * when you have come close enough for it to mean one particular village.
 */
const LABEL_VILLAGES = 4;

/**
 * And how far in the хати themselves are drawn.
 *
 * The tiles are painted from `probeColumn`, which is the *ground* - noise,
 * and it knows nothing about anything built on it. So a village never
 * appears on this map however far you zoom in: the country under it is
 * drawn correctly and the village is invisible. "Хочу подивитись як вони
 * виглядають" is not answered by a ring.
 *
 * A village is arithmetic, though - `planVillage` is the same call the
 * generator makes and it needs no world at all - so its plan can simply be
 * drawn over the ground. What you get is the thing from above: хати round a
 * square, городи behind them, the млин out on its own, and a тин round the
 * lot.
 */
const PLAN_VILLAGES = 4;

/** What each kind of building is, looked down on. */
const VILLAGE_COLOURS: Record<Kind, string> = {
  "хата": "#e8e2d4",
  "велика хата": "#efe9dc",
  "козацький": "#d8cdb6",
  "господарська": "#9a7b52",
  "млин": "#b08a55",
  "каплиця": "#f2eee4",
  "криниця": "#4d8ec4",
  "город": "#6b5334",
  "дороги": "#b9a97f",
  "тин": "#7a6242",
};

/**
 * One village's plan, over the country it stands on.
 *
 * The тин and the lanes are drawn first and outlined rather than filled -
 * their boxes are the whole village, so filling them would paint over
 * everything inside them.
 */
function drawVillage(at: { x: number; z: number }): void {
  const sx = toScreenX(at.x), sz = toScreenZ(at.z);
  const reach = VILLAGE_REACH / step;
  if (sx + reach < 0 || sz + reach < 0 ||
      sx - reach > innerWidth || sz - reach > innerHeight) {
    return;
  }
  const village = planVillage(seed, at);
  const order: Kind[] = ["тин", "дороги", "город"];
  for (const pass of [true, false]) {
    for (const part of village.parts) {
      if (order.includes(part.kind) !== pass) continue;
      const x = toScreenX(part.x), z = toScreenZ(part.z);
      const w = Math.max(1, part.w / step), d = Math.max(1, part.d / step);
      if (part.kind === "тин" || part.kind === "дороги") {
        ctx.strokeStyle = VILLAGE_COLOURS[part.kind];
        ctx.lineWidth = 1;
        ctx.strokeRect(x, z, w, d);
        continue;
      }
      ctx.fillStyle = VILLAGE_COLOURS[part.kind];
      ctx.fillRect(x, z, w, d);
    }
  }
}

/**
 * A ring and a name, drawn over the country rather than in it.
 *
 * `label` is what is written beside it and `name` is what it is - they are
 * the same for the things there is one of, and differ for a село, which is
 * always a ring and only sometimes a word.
 */
function mark(
  x: number, z: number, colour: string, name: string, label = name,
): void {
  const sx = toScreenX(x), sz = toScreenZ(z);
  if (sx < -80 || sz < -40 || sx > innerWidth + 80 || sz > innerHeight + 40) {
    return;
  }
  const r = name === "село" ? 4 : 6;
  ctx.lineWidth = 2;
  ctx.strokeStyle = "#0e1116";
  ctx.beginPath(); ctx.arc(sx, sz, r + 1, 0, Math.PI * 2); ctx.stroke();
  ctx.strokeStyle = colour;
  ctx.beginPath(); ctx.arc(sx, sz, r, 0, Math.PI * 2); ctx.stroke();
  if (label === "") return;
  ctx.font = "12px ui-monospace, Menlo, monospace";
  ctx.textAlign = "center";
  ctx.lineWidth = 3;
  ctx.strokeStyle = "#0e1116";
  ctx.strokeText(label, sx, sz - r - 6);
  ctx.fillStyle = colour;
  ctx.fillText(label, sx, sz - r - 6);
}

/** The скарб nearest the middle of the view, or null while they are dug up. */
function nearestTreasure(): TreasureFacts["list"][number] | null {
  let best: TreasureFacts["list"][number] | null = null;
  let far = Infinity;
  for (const t of treasures) {
    const d = Math.hypot(t.x - midX, t.z - midZ);
    if (d < far) { far = d; best = t; }
  }
  return best;
}

/** The село nearest the middle of the view, which is what a button jumps to. */
function nearestVillage(): { x: number; z: number } | null {
  if (facts === null || facts.villages.length === 0) return null;
  let best = facts.villages[0]!;
  let far = Infinity;
  for (const v of facts.villages) {
    const d = Math.hypot(v.x - midX, v.z - midZ);
    if (d < far) { far = d; best = v; }
  }
  return best;
}

function legend(): void {
  const rows: string[] = [
    `<div><i style="background:#587c3e"></i> луки</div>`,
    `<div><i style="background:#7a766e"></i> гори</div>`,
    `<div><i style="background:#ced6dc"></i> зима</div>`,
    `<div><i style="background:#c4ae74"></i> пустеля</div>`,
    `<div><i style="background:#1e3856"></i> вода</div>`,
    `<div><i style="background:#9bd150"></i> село</div>`,
    `<div><i style="background:#ffc83d"></i> скарб</div>`,
    `<div><i style="background:#ff6a3d"></i> лігво Змія</div>`,
  ];
  legendBox.innerHTML = rows.join("");
}

/** The scale, said the way round that gives a whole number. */
function scaleSays(): string {
  if (step >= 1) return `${step} ${step === 1 ? "блок" : "блоків"} / піксель`;
  const px = Math.round(1 / step);
  return `${px} ${px === 2 ? "пікселі" : "пікселів"} / блок`;
}

function say(): void {
  const lines = [`сід ${seed}`];
  if (facts === null) lines.push("рахую світ…");
  else {
    lines.push(`старт  x ${facts.home.x}  z ${facts.home.z}`);
    if (facts.gate === null) lines.push("замку немає");
    else {
      const d = Math.hypot(facts.gate.x - facts.home.x,
        facts.gate.z - facts.home.z);
      lines.push(`брама  x ${facts.gate.x}  y ${facts.gate.y}  z ${facts.gate.z}`);
      lines.push(`       ${d.toFixed(0)} блоків від старту`);
      if (facts.road !== null) {
        lines.push(`дорога x ${facts.road.x}  z ${facts.road.z}`);
      }
    }
  }
  if (facts !== null) {
    const near = nearestVillage();
    if (near === null) lines.push("сіл немає");
    else {
      lines.push(`село   x ${near.x}  z ${near.z}   (${facts.villages.length} на мапі)`);
    }
    let den: WorldFacts["lairs"][number] | null = null;
    let denFar = Infinity;
    for (const l of facts.lairs) {
      const d = Math.hypot(l.x - midX, l.z - midZ);
      if (d < denFar) { denFar = d; den = l; }
    }
    if (den === null) lines.push("лігв немає");
    else {
      lines.push(`лігво  x ${den.x}  y ${den.y}  z ${den.z}   ` +
        `(${facts.lairs.length} на мапі)`);
    }
    const dig = nearestTreasure();
    if (dig === null) lines.push("скарби рахую…");
    else {
      lines.push(`скарб  x ${dig.x}  y ${dig.y}  z ${dig.z}   ` +
        `(${dig.depth} під землею, ${treasures.length} на мапі)`);
    }
  }
  lines.push(`курсор x ${Math.round(cursorX)}  z ${Math.round(cursorZ)}`);
  whereBox.textContent = lines.join("\n");
}

let cursorX = 0, cursorZ = 0;

/* ---- moving about ------------------------------------------------------ */

let dragging = false;
let lastX = 0, lastZ = 0;
canvas.addEventListener("pointerdown", (e) => {
  dragging = true; lastX = e.clientX; lastZ = e.clientY;
  canvas.classList.add("dragging");
  canvas.setPointerCapture(e.pointerId);
});
canvas.addEventListener("pointermove", (e) => {
  cursorX = toBlockX(e.clientX);
  cursorZ = toBlockZ(e.clientY);
  if (dragging) {
    midX -= (e.clientX - lastX) * step;
    midZ -= (e.clientY - lastZ) * step;
    lastX = e.clientX; lastZ = e.clientY;
    epoch++;
    draw();
  }
  say();
});
const stopDrag = (): void => {
  dragging = false; canvas.classList.remove("dragging");
};
canvas.addEventListener("pointerup", stopDrag);
canvas.addEventListener("pointercancel", stopDrag);

/**
 * Zoom about the cursor.
 *
 * Keeping the block under the pointer *under the pointer* is the whole
 * difference between a map you can search with and one that throws you
 * somewhere else every time you turn the wheel.
 */
function zoom(by: number, atX: number, atZ: number): void {
  const was = step;
  const i = STEPS.indexOf(step as typeof STEPS[number]);
  const next = STEPS[Math.max(0, Math.min(STEPS.length - 1, i + by))]!;
  if (next === was) return;
  const bx = toBlockX(atX), bz = toBlockZ(atZ);
  step = next;
  midX = bx - (atX - innerWidth / 2) * step;
  midZ = bz - (atZ - innerHeight / 2) * step;
  scaleLabel.textContent = scaleSays();
  epoch++;
  draw();
  say();
}
/*
 * A notch of wheel, whatever the wheel is.
 *
 * "Apple magic mouse scroll гіперчутливий на сід мапі" - and it was: one
 * zoom step per event is right for a wheel that clicks, and a Magic Mouse or
 * a trackpad sends a stream of small deltas per gesture and then keeps
 * sending them after the finger has left. A flick went from the whole world
 * to a block per pixel and back.
 *
 * So the deltas are added up and a step is taken when they amount to a
 * notch, which is how a wheel behaves already: a real one sends about 120
 * pixels in one event and steps at once. Three things hold the rest of it
 * down - the running total is dropped when the direction changes or the
 * gesture stops, so a flick one way then the other does not cancel out; and
 * no more than one step is taken per event and none within 90ms of the last,
 * which is what turns momentum into a slow glide instead of a jump to the
 * end.
 */
const WHEEL_NOTCH = 120;
const WHEEL_GAP = 90;
let wheelAdds = 0;
let wheelLast = 0;
let wheelStepped = 0;
canvas.addEventListener("wheel", (e) => {
  e.preventDefault();
  const now = performance.now();
  // Lines and pages are worth many pixels each, and Firefox sends lines.
  const by = e.deltaMode === 1 ? e.deltaY * 16
    : e.deltaMode === 2 ? e.deltaY * 400
    : e.deltaY;
  if (now - wheelLast > 250 || Math.sign(by) !== Math.sign(wheelAdds)) {
    wheelAdds = 0;
  }
  wheelLast = now;
  wheelAdds += by;
  if (Math.abs(wheelAdds) < WHEEL_NOTCH) return;
  if (now - wheelStepped < WHEEL_GAP) return;
  wheelStepped = now;
  const way = wheelAdds > 0 ? 1 : -1;
  wheelAdds = 0;
  zoom(way, e.clientX, e.clientY);
}, { passive: false });

/* ---- the bar ----------------------------------------------------------- */

const show = (): void => {
  const asked = Number(seedBox.value.trim());
  if (!Number.isFinite(asked)) return;
  seed = Math.trunc(asked);
  facts = null;
  epoch++;
  startPool();
  draw();
  say();
  history.replaceState(null, "", `#seed=${seed}`);
};
document.getElementById("show")!.addEventListener("click", show);
seedBox.addEventListener("keydown", (e) => { if (e.key === "Enter") show(); });
document.getElementById("random")!.addEventListener("click", () => {
  seedBox.value = String(Math.floor(Math.random() * 2 ** 31));
  show();
});
document.getElementById("in")!
  .addEventListener("click", () => zoom(-1, innerWidth / 2, innerHeight / 2));
document.getElementById("out")!
  .addEventListener("click", () => zoom(1, innerWidth / 2, innerHeight / 2));
document.getElementById("toStart")!.addEventListener("click", () => {
  if (facts === null) return;
  midX = facts.home.x; midZ = facts.home.z; epoch++; draw(); say();
});
document.getElementById("toCastle")!.addEventListener("click", () => {
  if (facts?.gate == null) return;
  midX = facts.gate.x; midZ = facts.gate.z; epoch++; draw(); say();
});
/*
 * To the nearest село, and to the next one after that.
 *
 * Nearest to the middle of the *view* rather than to the start, so pressing
 * it again after it has moved you goes somewhere new: the village you are
 * looking at is now the nearest one, so it skips to the next. That is what
 * turns one button into a way of walking round all of them, which is what
 * somebody who wants to see what a село looks like actually needs.
 */
document.getElementById("toVillage")!.addEventListener("click", () => {
  if (facts === null || facts.villages.length === 0) return;
  let best: { x: number; z: number } | null = null;
  let far = Infinity;
  for (const v of facts.villages) {
    const d = Math.hypot(v.x - midX, v.z - midZ);
    if (d > 8 && d < far) { far = d; best = v; }
  }
  if (best === null) return;
  midX = best.x; midZ = best.z;
  // Close enough that the хати are houses rather than a smudge: a village is
  // 140 blocks across, so half a block to the pixel puts the whole of it in
  // the window with the buildings still readable.
  step = 0.5;
  scaleLabel.textContent = scaleSays();
  epoch++; draw(); say();
});
/** To the nearest лігво, and to the next one after that. Same rule again. */
document.getElementById("toLair")!.addEventListener("click", () => {
  if (facts === null || facts.lairs.length === 0) return;
  let best: { x: number; z: number } | null = null;
  let far = Infinity;
  for (const l of facts.lairs) {
    const d = Math.hypot(l.x - midX, l.z - midZ);
    if (d > 8 && d < far) { far = d; best = l; }
  }
  if (best === null) return;
  midX = best.x; midZ = best.z;
  step = 1;
  scaleLabel.textContent = scaleSays();
  epoch++; draw(); say();
});
/** To the nearest скарб, and to the next one after that. Same rule as села. */
document.getElementById("toTreasure")!.addEventListener("click", () => {
  if (treasures.length === 0) return;
  let best: { x: number; z: number } | null = null;
  let far = Infinity;
  for (const t of treasures) {
    const d = Math.hypot(t.x - midX, t.z - midZ);
    if (d > 8 && d < far) { far = d; best = t; }
  }
  if (best === null) return;
  midX = best.x; midZ = best.z;
  step = 0.5;
  scaleLabel.textContent = scaleSays();
  epoch++; draw(); say();
});
document.getElementById("copy")!.addEventListener("click", async () => {
  const link = `${location.origin}${location.pathname}#seed=${seed}`;
  try { await navigator.clipboard.writeText(link); } catch { /* no clipboard */ }
});

/* ---- and what it opens on ---------------------------------------------- */

const fromHash = /seed=(-?\d+)/.exec(location.hash);
if (fromHash !== null) {
  seed = Number(fromHash[1]);
  seedBox.value = String(seed);
}
addEventListener("resize", resize);
scaleLabel.textContent = `${step} блоків / піксель`;
startPool();
resize();
say();
